import { animate, View } from '@play-co/timestep-core/ui';
import ResponsiveScene from '../scenes/ResponsiveScene';
import uiConfig from 'src/lib/ui/config';
import LivingRoomDecorationView from './LivingRoomDecorationView';
import {
  DecorationItem,
  DecorationItemID,
  DecorationRoomID,
  DecorationSlot,
  DecorationTierID,
} from 'src/replicant/decoration/decoration.ruleset';
import DecorationView from './DecorationView';
import { assertNever } from 'src/replicant/utils';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import DecorationSelectItemView, {
  DecorationListItem,
} from './DecorationSelectItemView';
import bitmapFonts from 'src/lib/bitmapFonts';
import StateObserver from 'src/StateObserver';
import {
  animDuration,
  getApplication,
  getScreenDimensions,
} from 'src/lib/utils';
import {
  decorationFinishEvent,
  decorationStartEvent,
} from './decoration.analytics';
import {
  getSelectedItems,
  isDecorationGachaEventAvailable,
  isDecorationTutorialInProgress,
} from './helpers';
import { decorationTutorialInProgressSequence } from './decoration.sequence';
import ToiletRoomDecorationView from './ToiletRoomDecorationView';
import { GachaButton } from './GachaButton';

/**
 * This scene allow user to select decorations for rooms
 */
export default class DecorationEditScene extends ResponsiveScene {
  // Current room id
  private room: DecorationRoomID;

  // Current decoration background
  private decorationView!: DecorationView;

  // Items selection UI
  private selectItem: DecorationSelectItemView;

  // List of edit buttons
  private buttons: Map<DecorationSlot, ButtonScaleViewWithText> = new Map();

  // List of equipped preview items(temporary)
  private preview: Map<
    DecorationSlot,
    DecorationItem & { tierId?: DecorationTierID }
  > = new Map();

  // List of selected items(saved in state)
  private selected: Map<
    DecorationSlot,
    DecorationItem & { tierId?: DecorationTierID }
  > = new Map();

  // Main control buttons
  private controls: {
    container: FlexBoxView;
    ok?: ButtonScaleViewWithText;
    cancel?: ButtonScaleViewWithText;
    back?: ButtonScaleViewWithText;
  };

  // Gacha button.
  private gachaButton!: View;

  /**
   * Constructor
   */
  constructor(room: DecorationRoomID) {
    super(new View({ width: uiConfig.width, height: uiConfig.height }));

    // Set selected room
    this.room = room;

    // Set main control buttons (save and cancel)
    this.controls = this.createMainControls(this.getView());

    // Items selection bottom UI
    this.selectItem = new DecorationSelectItemView({
      superview: this.getView(),
      offsetY: getScreenDimensions().height - 240,
      width: uiConfig.width,
      height: 240,
      zIndex: 999,
    });
  }

  /**
   * Choose decoration room layout
   */
  private getDecorationView(room: DecorationRoomID): DecorationView {
    // Select new one
    switch (room) {
      case 'living':
        return new LivingRoomDecorationView({
          autoDecorationUpdate: true,
        });
      case 'toilet':
        return new ToiletRoomDecorationView({
          autoDecorationUpdate: true,
        });
      default:
        assertNever(room);
    }
  }

  /**
   * On scene show
   */
  public showScene() {
    // By default living room
    this.decorationView = this.getDecorationView(this.room);

    // Set decoration view
    this.getView().addSubview(this.decorationView);

    // Show change slot image buttons
    this.createEditSlotButtons(this.getView(), this.decorationView);

    // Get selected items
    this.selected = getSelectedItems(this.decorationView.getRoom());
    this.preview = new Map(this.selected);

    // Listen select events
    this.selectItem.subscribe('selected', async (item: DecorationListItem) => {
      await this.equipItem(item);
    });

    // Hide selection on empty space click
    this.decorationView.subscribe(
      'InputSelect',
      this.closeChangeSlotUI.bind(this),
    );

    // Restore state
    this.controls.ok?.setDisabled(false);
    this.controls.cancel?.setDisabled(false);
    this.controls.back?.setDisabled(false);
    this.controls.container.style.offsetY = 0;
    this.controls.container.style.opacity = 1;

    // Decoration start analytics event
    decorationStartEvent(this.room);

    // Show tutorial
    if (isDecorationTutorialInProgress()) {
      decorationTutorialInProgressSequence();
    }

    this.createGachaButtons();
  }

  /**
   * On scene hide
   */
  public hideScene() {
    this.selectItem.removeAllListeners('selected');
    this.decorationView.removeAllListeners('InputSelect');

    this.buttons.forEach((button) => {
      this.getView().removeSubview(button);
    });

    this.buttons.clear();
    this.selected.clear();

    this.getView().removeSubview(this.decorationView);

    this.gachaButton?.removeFromSuperview();
  }

  /**
   * On items confirm button click
   */
  private async confirmSequence() {
    // Close ui
    await this.closeChangeSlotUI();

    // Equip selected items
    const preview = [...this.preview.values()].map((item) =>
      item.tierId
        ? {
            itemId: item.id,
            tierId: item.tierId,
          }
        : { itemId: item.id },
    );
    await StateObserver.invoke.equipDecorationItems({
      room: this.room,
      items: preview,
    });

    // Get diff between selected and equipped in this session
    const changed = new Set<DecorationSlot>();
    this.decorationView.getSlotsIDs().forEach((slot) => {
      const a = this.selected.get(slot);
      const b = this.preview.get(slot);

      if (a?.id === b?.id) return;

      changed.add(slot);
    });

    // Analytics event

    decorationFinishEvent({
      room: this.room,
      canceled: false,
      slotsEdited: changed.size,
    });

    await getApplication().screenManager.showScreen('pet');
  }

  /**
   * User pressed cancel button
   */
  private async cancelEdit() {
    await this.closeChangeSlotUI();

    // Analytics event
    decorationFinishEvent({
      room: this.room,
      canceled: true,
      slotsEdited: 0,
    });

    await getApplication().screenManager.showScreen('pet');
  }

  /**
   * Set item preview
   */
  private async equipItem(item: DecorationListItem) {
    const slot = item.slot;
    const defaultID = 'default' as DecorationItemID;
    const preview = this.preview.get(slot);

    // Remove previous choice
    if (preview) {
      this.preview.delete(slot);
    }

    // Set new choice for this slot, if it not default
    if (item.id !== defaultID) {
      this.preview.set(slot, item);
    }

    // Play sound
    sounds.playSound('deco');

    // Animate slot if item changed
    await this.decorationView.updateImageViewWithAnimation(item.slot, item);
  }

  /**
   * Show hidden edit buttons
   */
  private restoreEditButtons() {
    this.buttons.forEach((button) => {
      button.show();
      button.setDisabled(false);

      animate(button)
        .commit()
        .then({ scale: 1 }, animDuration, animate.easeOutExpo);
    });
  }

  /**
   * Show decoration slots UI
   */
  private async showChangeSlotUI(slot: DecorationSlot, room: DecorationRoomID) {
    // Show all edit buttons
    this.restoreEditButtons();

    // Hide pressed edit button
    const button = this.buttons.get(slot);
    if (!button) return;

    button.setDisabled(true);

    // Animate button hiding
    animate(button)
      .commit()
      .then({ scale: 0 }, animDuration, animate.easeInExpo)
      .then(() => {
        button.hide();
      });

    // Collect selected IDs
    const selected = this.selected.get(slot) ?? this.preview.get(slot) ?? null;

    // Get default slot icon
    const defaultSlotItemUrl = this.decorationView.getDefaultImage(
      slot,
      'classic',
    );

    // Slide gacha button up
    let gachaAnim: Promise<void> = Promise.resolve();
    if (this.gachaButton) {
      gachaAnim = animate(this.gachaButton)
        .then(
          {
            y: -235,
          },
          200,
        )
        .toPromise();
    }

    // Show selection UI
    await Promise.all([
      this.selectItem.open(slot, room, selected, defaultSlotItemUrl),
      gachaAnim,
    ]);
  }

  /**
   * Change hide slot content
   */
  private async closeChangeSlotUI() {
    sounds.playSound('cancel');

    let gachaAnim: Promise<void> = Promise.resolve();
    if (this.gachaButton) {
      gachaAnim = animate(this.gachaButton)
        .then(
          {
            y: 0,
          },
          200,
        )
        .toPromise();
    }

    await Promise.all([this.selectItem.close(), gachaAnim]);

    this.restoreEditButtons();
  }

  /**
   * Show change buttons under slots
   */
  private createEditSlotButtons(superview: View, layout: DecorationView) {
    // Remove all previous buttons and icons
    this.buttons.clear();

    const createButton = this.createEditButton.bind(this);

    layout.getSlotsIDs().forEach((slot) => {
      // Edit slot button
      this.buttons.set(slot, createButton(superview, slot, layout));
    });
  }

  private createEditButton(
    superview: View,
    slot: DecorationSlot,
    layout: DecorationView,
  ): ButtonScaleViewWithText {
    return new ButtonScaleViewWithText({
      superview,
      image: 'assets/ui/decoration/buttons/btn_change.png',
      imagePressed: 'assets/ui/decoration/buttons/btn_change_pressed.png',
      imageDisabled: 'assets/ui/decoration/buttons/btn_change_disabled.png',
      scaleMethod: '3slice',
      labelOffsetY: -2,
      labelOffsetX: 10,
      fontSize: 30,
      font: bitmapFonts('Body'),
      sourceSlices: { horizontal: { left: 90, right: 50 } },
      width: 170,
      height: 73,
      ...layout.getButtonsConfig(slot),
      centerAnchor: true,
      localeText: () => i18n('basic.edit'),
      tracking: {
        feature: Feature.DECORATION._,
        $subFeature: Feature.DECORATION.CHANGE_BUTTON,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        // Open edit UI
        await this.showChangeSlotUI(slot, layout.getRoom());
      },
    });
  }

  /**
   * Create cancel and save buttons
   */
  private createMainControls(superview: View) {
    const { user } = StateObserver.getState();

    const container = new FlexBoxView({
      superview,
      width: superview.style.width,
      height: 107,
      x: 0,
      y: 25,
      flex: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingHorizontal: 50,
      },
      zIndex: 1000,
    });

    let ok: ButtonScaleViewWithText | undefined = undefined;
    let back: ButtonScaleViewWithText | undefined = undefined;
    let cancel: ButtonScaleViewWithText | undefined = undefined;

    cancel = new ButtonScaleViewWithText({
      superview: container,
      ...uiConfig.buttons.cancel,
      width: 284,
      height: 107,
      fontSize: 60,
      labelOffsetX: -6,
      labelOffsetY: -1,
      font: bitmapFonts('Body'),
      localeText: () => i18n('basic.cancel'),
      tracking: {
        feature: Feature.DECORATION._,
        $subFeature: Feature.DECORATION.CANCEL,
      },
      onClick: async () => {
        sounds.sfx.play('cancel');
        await this.cancelEdit();
      },
    });

    ok = new ButtonScaleViewWithText({
      superview: container,
      ...uiConfig.buttons.secondary,
      fontSize: 60,
      width: 284,
      height: 107,
      labelOffsetX: -6,
      labelOffsetY: -1,
      font: bitmapFonts('Body'),
      localeText: () => i18n('basic.ok'),
      tracking: {
        feature: Feature.DECORATION._,
        $subFeature: Feature.DECORATION.SAVE,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.confirmSequence();
      },
    });

    return { container, ok, cancel, back };
  }

  private createGachaButtons() {
    this.gachaButton = this.getView().addSubview(
      new GachaButton({ ignoreThemeOffsets: true }),
    );

    this.gachaButton.updateOpts({
      zIndex: 999,
      x: uiConfig.width - this.gachaButton.style.width - 5,
      offsetY:
        getScreenDimensions().height - this.gachaButton.style.height - 10,
    });
  }
}
